import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import LeadershipLadder from './LeadershipLadder';
import LineChart from './LineChart';
import { fmt, periodLabel } from './format';
import { businessPanel } from './BusinessesPage';
import { SHAPES, VIEWBOX, MAP_CREDIT } from './nigeriaMap';
const api = () => app.forum.attribute('apiUrl') + '/datahub/';
const go = (e, url) => {
  if (e.ctrlKey || e.metaKey || e.shiftKey) return;
  e.preventDefault();
  m.route.set(url);
};
const link = (url, children, cls) => m("a", {
  className: cls,
  href: url,
  onclick: e => go(e, url)
}, children);

/** Kinds of Atlas points: label and colour */
const KINDS = {
  resource: ['Mineral & energy resources', '#b45309'],
  crop: ['Crops & farming', '#3f8f3f'],
  facility: ['Industry & facilities', '#2563eb'],
  port: ['Ports', '#0e7490']
};
const kindOf = k => KINDS[k] || ['Other', '#6b7280'];
const SECTIONS = [['overview', 'Key figures'], ['leadership', 'Leadership'], ['map', 'Map & resources'], ['investments', 'Investments'], ['community', 'Community projects'], ['businesses', 'Businesses'], ['budget', 'Budget'], ['security', 'Security'], ['elections', 'Elections'], ['energy', 'Power'], ['education', 'Education'], ['neighbours', 'Neighbours']];

/** Atlas power plants in the state, largest installed capacity first (from "Gas · 1,320 MW installed") */
const plantMw = p => {
  const m = /([\d,.]+)\s*MW/i.exec(p.what || '');
  return m ? parseFloat(m[1].replace(/,/g, '')) : 0;
};
const plants = d => (d.resources && d.resources.items || []).filter(x => x.kind === 'facility' && x.type === 'power_plant').sort((a, b) => plantMw(b) - plantMw(a));
const dateLabel = iso => iso ? new Date(iso).toLocaleDateString('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric'
}) : '';
const monthShort = ym => periodLabel(ym).split(' ')[0];
const pct = (a, b) => b ? Math.round((a - b) / Math.abs(b) * 1000) / 10 : null;

/** "Ad space available" card (same as the theme's), shown when no ad is booked and the placeholder is on */
function adPlaceholder(el) {
  if (!app.forum.attribute('vouchaAdsPlaceholder')) return;
  el.innerHTML = '<a class="VouchaAdOpen" href="/advertise">' + '<span class="VouchaAdOpen-label">Ad space available</span>' + '<strong>Reach Nigerians at home and abroad</strong>' + '<span class="VouchaAdOpen-text">Your brand here, beside the tools and data our readers use every day.</span>' + '<span class="VouchaAdOpen-cta">Advertise on Voucha <i class="fas fa-arrow-right" aria-hidden="true"></i></span>' + '</a>';
}

/**
 * An ad slot inside the page (voucha/ads fills it; Mithril leaves its contents alone): the wide in-page banner
 * (desktop and mobile images); if none is booked here, a sidebar ad (centred), then the "Ad space available" card.
 */
const InPageAd = {
  oncreate(vnode) {
    if (app.vouchaAds && app.vouchaAds.fillSlot) {
      const tag = vnode.attrs.tag || '';
      const sidebar = el => app.vouchaAds.fillSlot(el, {
        placement: 'banner',
        tool: 'data-hub',
        tag,
        empty: adPlaceholder
      });
      app.vouchaAds.fillSlot(vnode.dom, {
        placement: 'page_banner',
        tool: 'data-hub',
        tag,
        empty: sidebar
      });
    }
  },
  onbeforeupdate() {
    return false;
  },
  view() {
    return m("div", {
      className: "DHState-ad"
    });
  }
};

/** /data-hub/states/{state}: one page with everything Voucha knows about a state */
export default class StatePage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'DataHubFull';
    this.load(m.route.param('slug'));
  }
  load(slug) {
    this.slug = slug;
    this.data = null;
    this.error = null;
    this.shape = null;
    this.hoverItem = null;
    this.kindsOff = {};
    app.setTitle('State profile');
    app.request({
      method: 'GET',
      url: api() + 'states/' + encodeURIComponent(slug),
      errorHandler: () => {}
    }).then(r => {
      if (slug !== this.slug) return;
      this.data = r;
      app.setTitle(this.title());
      if (r.state.placeSlug) {
        app.request({
          method: 'GET',
          url: api() + 'places/' + encodeURIComponent(r.state.placeSlug) + '/shape',
          errorHandler: () => {}
        }).then(s => {
          if (slug === this.slug) {
            this.shape = s;
            m.redraw();
          }
        }).catch(() => {
          if (slug === this.slug) {
            this.shape = false;
            m.redraw();
          }
        });
      }
    }).catch(() => {
      this.error = true;
    }).finally(() => m.redraw());
  }
  title() {
    const s = this.data && this.data.state;
    return s ? s.isFct ? 'FCT Abuja' : s.name + ' State' : 'State profile';
  }
  view() {
    // moving between states (e.g. a neighbour's link) reuses this page: load the new one
    const slug = m.route.param('slug');
    if (slug && slug !== this.slug) this.load(slug);
    if (this.error) {
      return m("div", {
        className: "DataHubFull-page"
      }, m("div", {
        className: "container DHState"
      }, link('/data-hub/states', [m("i", {
        className: "fas fa-arrow-left"
      }), ' All states'], 'DataHubPub-back'), m("p", {
        className: "DHMuted"
      }, "We couldn't find that state.")));
    }
    if (!this.data) return m("div", {
      className: "DataHubFull-page"
    }, m("div", {
      className: "container DHState"
    }, m(LoadingIndicator, null)));
    const d = this.data;
    const has = this.available(d);
    return m("div", {
      className: "DataHubFull-page"
    }, this.hero(d), m("nav", {
      className: "DHState-nav"
    }, m("div", {
      className: "container"
    }, SECTIONS.filter(([k]) => has[k]).map(([k, label]) => m("a", {
      href: '#state-' + k,
      onclick: e => {
        e.preventDefault();
        this.jump(k);
      }
    }, label)))), m("div", {
      className: "container DHState"
    }, has.overview ? this.overview(d) : null, has.leadership ? this.leadership(d) : null, app.vouchaAds && app.vouchaAds.fillSlot ? m(InPageAd, {
      key: 'ad-' + d.state.slug,
      tag: d.state.tagSlug
    }) : null, has.map ? this.mapSection(d) : null, this.investments(d), this.communityProjects(d), this.businesses(d), has.budget ? this.budget(d) : null, has.security ? this.security(d) : null, has.elections ? this.elections(d) : null, has.energy ? this.energy(d) : null, has.education ? this.education(d) : null, has.neighbours ? this.neighbours(d) : null, m("p", {
      className: "DataHubPub-source"
    }, "Figures from the Voucha Data Hub (NBS, OAGF, DMO, NPC and the sources on each dataset), resources from the Voucha Atlas, stories from the forum. Boundary: geoBoundaries (CC BY 4.0). ", MAP_CREDIT, ".")));
  }

  /** Which sections have something to show */
  available(d) {
    const mt = d.metrics || {};
    const any = group => Object.values(mt).some(x => x.group === group);
    return {
      overview: true,
      leadership: !!d.leadership,
      map: !!d.state.placeSlug,
      investments: true,
      businesses: true,
      budget: any('budget'),
      security: !!(d.security && d.security.enabled && ((d.security.latest || []).length || (d.security.monthly || []).some(r => r.count))),
      elections: (d.elections || []).length > 0 || (d.polls || []).length > 0,
      energy: any('energy') || plants(d).length > 0,
      education: any('education'),
      neighbours: (d.neighbours || []).length > 0 || any('trade')
    };
  }
  jump(k) {
    const el = document.getElementById('state-' + k);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - 120;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  }
  section(key, title, sub, body, extra) {
    return m("section", {
      className: "DHSection DHState-section",
      id: 'state-' + key
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, title), sub ? m("p", {
      className: "DHMuted"
    }, sub) : null), extra || null), body);
  }

  // ---------------- hero ----------------
  hero(d) {
    const s = d.state;
    const g = d.leadership;
    const pop = (d.metrics || {}).population_2022;
    return m("section", {
      className: "DHHero DHState-hero"
    }, m("div", {
      className: "container DHHero-inner"
    }, m("div", {
      className: "DHHero-text"
    }, link('/data-hub/states', [m("i", {
      className: "fas fa-arrow-left"
    }), ' All states'], 'DataHubPub-back'), m("div", {
      className: "DHHero-eyebrow"
    }, s.zone ? s.zone + ' · ' : '', "State profile"), m("h1", null, this.title(), g && g.rank ? m("a", {
      className: "DHTagRank",
      href: "/community/governors",
      title: 'Ranked ' + g.rank + ' of ' + g.of + ' states by overall score',
      onclick: e => go(e, '/community/governors')
    }, "#", g.rank) : null), app.vouchaPageText && app.vouchaPageText('/data-hub/states/' + s.slug, 'DHState-summary') || m("p", null, s.capital ? 'Capital: ' + s.capital : ''), m("div", {
      className: "DHState-heroFacts"
    }, g && g.name ? m("span", {
      className: "DHChip"
    }, m("i", {
      className: "fas fa-user-tie"
    }), " ", g.role, ": ", m("strong", null, g.name), g.party ? ' (' + g.party + ')' : '') : null, pop ? m("span", {
      className: "DHChip"
    }, m("i", {
      className: "fas fa-users"
    }), " ", m("strong", null, fmt(pop.value, 'people', true)), " people") : null, (d.lgas || []).length ? m("span", {
      className: "DHChip"
    }, m("i", {
      className: "fas fa-map-signs"
    }), " ", m("strong", null, d.lgas.length), " LGAs") : null, d.members && d.members.liveHere ? m("span", {
      className: "DHChip"
    }, m("i", {
      className: "fas fa-user-friends"
    }), " ", m("strong", null, d.members.liveHere.toLocaleString('en-US')), " Voucha ", d.members.liveHere === 1 ? 'member lives' : 'members live', " here") : null, d.members && d.members.fromHere ? m("span", {
      className: "DHChip"
    }, m("i", {
      className: "fas fa-home"
    }), " ", m("strong", null, d.members.fromHere.toLocaleString('en-US')), " ", d.members.fromHere === 1 ? 'member is' : 'members are', " from ", s.isFct ? 'the FCT' : s.name, d.members.fromHereAbroad ? ' (' + d.members.fromHereAbroad.toLocaleString('en-US') + ' abroad)' : '') : null)), m("div", {
      className: "DHState-locator"
    }, this.locator(d))));
  }

  /** Nigeria with this state highlighted and its neighbours tinted */
  locator(d) {
    const near = new Set((d.neighbours || []).map(n => n.slug));
    return m("svg", {
      viewBox: VIEWBOX,
      role: "img",
      "aria-label": 'Where ' + d.state.name + ' is in Nigeria'
    }, Object.entries(SHAPES).map(([slug, path]) => m("path", {
      d: path,
      className: slug === d.state.slug ? 'is-this' : near.has(slug) ? 'is-near' : '',
      onclick: () => slug !== d.state.slug && m.route.set('/data-hub/states/' + slug)
    }, m("title", null, slug === d.state.slug ? d.state.name : slug.replace(/-/g, ' ')))));
  }

  // ---------------- key figures ----------------
  tile(x, opts = {}) {
    if (!x) {
      if (!opts.always) return null;
      return m("div", {
        className: "DHKpi DHState-tile is-empty"
      }, m("div", {
        className: "DHKpi-head"
      }, opts.label), m("div", {
        className: "DHKpi-value"
      }, "\u2014"), m("div", {
        className: "DHState-tileMeta"
      }, m("span", null, "No figure yet")));
    }
    const change = x.prev !== null && x.prev !== undefined ? pct(x.value, x.prev) : null;
    return m("div", {
      className: "DHKpi DHState-tile"
    }, m("div", {
      className: "DHKpi-head"
    }, opts.label || x.label), m("div", {
      className: "DHKpi-value"
    }, fmt(x.value, x.unit === 'people' ? 'people' : x.unit, true)), m("div", {
      className: "DHState-tileMeta"
    }, x.period || opts.forcePeriod ? m("span", null, opts.periodText || periodLabel(x.period)) : null, change !== null ? m("span", {
      className: change >= 0 ? 'is-up' : 'is-down'
    }, change >= 0 ? '▲' : '▼', " ", Math.abs(change), "% vs ", periodLabel(x.prevPeriod)) : null, x.rank && x.of > 1 ? m("span", {
      className: "DHState-rank"
    }, "#", x.rank, " of ", x.of, " states") : null), opts.note ? m("div", {
      className: "DHKpi-source"
    }, opts.note) : null);
  }
  overview(d) {
    const mt = d.metrics;
    // FAAC is uploaded month by month, so the figure covers however many months are loaded (up to 12).
    // Name that period exactly. Against a year of IGR, compare a year of FAAC: the 12 months when loaded,
    // otherwise an estimate (the monthly average × 12, as the governors' ranking does), labelled as one
    const faac = mt.faac_state;
    const months = faac ? faac.months || 1 : 0;
    const faacLabel = months >= 12 ? 'FAAC allocation (12 months)' : months > 1 ? `FAAC allocation (${months} months)` : 'FAAC allocation (one month)';
    const faacPeriod = !faac ? null : months > 1 ? `${periodLabel(faac.firstPeriod)} to ${periodLabel(faac.period)}` : periodLabel(faac.period);
    const faacYear = faac && faac.value > 0 ? faac.value / months * 12 : null;
    const igrFaac = mt.state_igr && faacYear ? mt.state_igr.value / faacYear : null;
    return this.section('overview', 'Key figures', d.state.isFct ? 'The latest figure for each. The FCT isn\'t ranked against the states.' : 'The latest figure for each, with where ' + d.state.name + ' ranks among the 36 states.', m("div", {
      className: "DHKpis DHState-tiles"
    }, this.tile(mt.population_2022, {
      always: true,
      label: 'Population',
      periodText: '2022 projection',
      forcePeriod: true
    }), this.tile(mt.state_land_area, {
      always: true,
      label: 'Land area',
      note: mt.state_land_area && mt.state_land_area.estimated ? 'Worked out from the state boundary' : null
    }), this.tile(mt.state_igr, {
      always: true,
      label: 'Internally generated revenue'
    }), this.tile(faac, {
      always: true,
      label: faac ? faacLabel : 'FAAC allocation',
      periodText: faacPeriod
    }), this.tile(mt.state_gdp), igrFaac !== null ? m("div", {
      className: "DHKpi DHState-tile"
    }, m("div", {
      className: "DHKpi-head"
    }, "IGR as a share of FAAC", months >= 12 ? '' : ' (estimate)'), m("div", {
      className: "DHKpi-value"
    }, months >= 12 ? '' : 'about ', Math.round(igrFaac * 100), "%"), m("div", {
      className: "DHState-tileMeta"
    }, m("span", null, periodLabel(mt.state_igr.period), " IGR against ", months >= 12 ? 'FAAC for ' + faacPeriod : `${faacPeriod} FAAC × ${(12 / months).toFixed(months === 1 ? 0 : 1).replace(/\.0$/, '')}`, ":", ' ', igrFaac >= 1 ? 'raises more than it receives' : 'relies mainly on federal allocation'))) : null, this.tile(mt.state_debt_domestic), this.tile(mt.state_debt_external), this.tile(mt.state_budget_approved), this.tile(mt.state_universities, {
      label: 'Universities'
    })));
  }

  // ---------------- leadership ----------------
  leadership(d) {
    const g = d.leadership;
    const hist = (g.rankHistory || []).filter(h => h.rank);
    // movement since the previous quarter (only once there is one)
    const qMove = hist.length > 1 ? hist[hist.length - 2].rank - hist[hist.length - 1].rank : 0;
    return this.section('leadership', 'Leadership', 'Who runs ' + d.state.name + ' and how it ranks.', [m("div", {
      className: "DHPair"
    }, m("div", {
      className: "DHPanel DHState-governor"
    }, g.photo ? m("img", {
      className: "DHState-photo",
      src: g.photo,
      alt: g.name || '',
      loading: "lazy"
    }) : m("div", {
      className: "DHState-photo is-empty"
    }, m("i", {
      className: "fas fa-user"
    })), m("div", {
      className: "DHState-governorText"
    }, m("div", {
      className: "DHHero-eyebrow"
    }, g.role), m("h3", null, g.name || 'Not recorded yet'), m("p", {
      className: "DHMuted"
    }, [g.party, g.since ? 'in office since ' + dateLabel(g.since) : null].filter(Boolean).join(' · ')), g.education ? m("p", null, m("strong", null, "Education:"), " ", g.education) : null, g.previousRoles ? m("p", null, m("strong", null, "Before:"), " ", g.previousRoles) : null, g.bio ? m("p", null, g.bio) : null, g.predecessor ? m("p", {
      className: "DHMuted"
    }, "Predecessor: ", g.predecessor.name, g.predecessor.until ? ' (to ' + dateLabel(g.predecessor.until) + ')' : '') : null, link('/community/governors', ['All governors and the ranking ', m("i", {
      className: "fas fa-arrow-right"
    })], 'DHLink'))), m("div", {
      className: "DHPanel"
    }, m("h3", null, "State ranking"), m("p", {
      className: "DHMuted"
    }, "Voucha's overall score for the 36 states (own revenue, revenue per person, independence from FAAC, and debt)."), g.rank ? m("div", {
      className: "DHState-bigRank"
    }, m("strong", null, "#", g.rank), " ", m("span", null, "of ", g.of), g.score !== null && g.score !== undefined ? m("span", {
      className: "DHChip"
    }, "score ", Math.round(g.score), "/100") : null, qMove ? m("span", {
      className: 'DHChip ' + (qMove > 0 ? 'is-up' : 'is-down')
    }, qMove > 0 ? '▲ up ' : '▼ down ', Math.abs(qMove), " since last quarter") : null) : m("p", {
      className: "DHMuted"
    }, d.state.slug === 'abuja' ? 'The FCT isn\'t ranked: it has no governor (a federal minister runs it) and is funded differently from the states.' : 'Not ranked yet.'), hist.length ? m("p", {
      className: "DHMuted DHState-note"
    }, "As of ", periodLabel(hist[hist.length - 1].period), ". Rankings are compared quarter by quarter.") : null, hist.length > 1 ? m(LineChart, {
      unit: "",
      series: [{
        id: 1,
        name: 'Rank by quarter',
        points: hist.map(h => ({
          period: h.period,
          value: h.rank
        }))
      }]
    }) : null, hist.length > 1 ? m("p", {
      className: "DHMuted DHState-note"
    }, "Lower is better: #1 is the top state.") : null)), d.ladder ? m("div", {
      className: "DHPanel DHState-ladder"
    }, m("h3", null, "Leadership ladder"), m("p", {
      className: "DHMuted"
    }, "Who governs ", d.state.name, ", from the ", d.state.isFct ? 'FCT Minister' : 'governor', " down to the wards. Each dot is a seat, filled when we know who holds it: tap a dot for the person, or a row for everyone."), m(LeadershipLadder, {
      ladder: d.ladder,
      state: d.state,
      editor: !!app.data.vouchaDataEditor
    })) : null]);
  }

  // ---------------- map & resources ----------------
  mapSection(d) {
    const r = d.resources || {
      items: [],
      commodities: []
    };
    const kinds = Array.from(new Set(r.items.map(i => i.kind)));
    const atlas = '/atlas?place=' + encodeURIComponent(d.state.placeSlug);
    return this.section('map', 'Map & resources', r.items.length ? r.items.length + ' places from the Voucha Atlas inside the state.' : 'Nothing from the Atlas inside the state yet.', m("div", {
      className: "DHPair"
    }, m("div", {
      className: "DHPanel DHState-mapPanel"
    }, this.stateMap(d, r.items), kinds.length ? m("div", {
      className: "DHState-kinds"
    }, kinds.map(k => m("button", {
      className: 'DHState-kind' + (this.kindsOff[k] ? ' is-off' : ''),
      onclick: () => this.kindsOff[k] = !this.kindsOff[k]
    }, m("span", {
      style: {
        background: kindOf(k)[1]
      }
    }), " ", kindOf(k)[0]))) : null, this.hoverItem ? m("p", {
      className: "DHState-hover"
    }, m("strong", null, this.hoverItem.name), this.hoverItem.what ? ' · ' + this.hoverItem.what : '', this.hoverItem.area ? ' · ' + this.hoverItem.area : '') : null), m("div", {
      className: "DHPanel"
    }, m("h3", null, "Key commodities"), m("p", {
      className: "DHMuted"
    }, "What the state's farms, mines and factories produce, from the Atlas."), r.commodities.length ? m("div", {
      className: "DHState-commodities"
    }, r.commodities.map(c => m("span", {
      className: "DHChip"
    }, c.name, c.count > 1 ? m("small", null, " \xD7", c.count) : null))) : m("p", {
      className: "DHMuted"
    }, "None recorded yet."), r.items.length ? m("ul", {
      className: "DHState-list"
    }, r.items.slice(0, 8).map(i => m("li", null, m("span", {
      className: "DHState-dot",
      style: {
        background: kindOf(i.kind)[1]
      }
    }), link('/atlas/' + i.id, i.name), m("span", {
      className: "DHMuted"
    }, [i.what, i.status && i.status !== 'operational' ? i.status : null].filter(Boolean).join(' · '))))) : null, m("a", {
      className: "Button DHLinkButton",
      href: atlas,
      onclick: e => go(e, atlas)
    }, m("i", {
      className: "fas fa-map"
    }), " Open ", d.state.name, " in the Atlas"))));
  }

  /** The state's outline with the Atlas's points (no map tiles needed) */
  stateMap(d, items) {
    const s = this.shape;
    if (!s) return m("div", {
      className: "DHState-mapEmpty"
    }, this.shape === null ? m(LoadingIndicator, null) : m("p", {
      className: "DHMuted"
    }, "The state's outline isn't available yet."));
    const [minLng, minLat, maxLng, maxLat] = s.bbox;
    const k = Math.cos((minLat + maxLat) / 2 * Math.PI / 180);
    const W = 600,
      P = 14;
    const scale = (W - P * 2) / Math.max(0.0001, (maxLng - minLng) * k);
    const H = Math.round((maxLat - minLat) * scale + P * 2);
    const x = lng => P + (lng - minLng) * k * scale;
    const y = lat => P + (maxLat - lat) * scale;
    const g = s.geometry || {};
    const polys = g.type === 'MultiPolygon' ? g.coordinates : g.type === 'Polygon' ? [g.coordinates] : [];
    const path = polys.map(poly => poly.map(ring => 'M' + ring.map(p => x(p[0]).toFixed(1) + ',' + y(p[1]).toFixed(1)).join('L') + 'Z').join('')).join('');
    return m("svg", {
      className: "DHState-map",
      viewBox: `0 0 ${W} ${H}`,
      role: "img",
      "aria-label": 'Map of ' + d.state.name
    }, m("path", {
      d: path,
      className: "DHState-outline"
    }), items.filter(i => !this.kindsOff[i.kind]).map(i => m("circle", {
      cx: x(i.lng),
      cy: y(i.lat),
      r: this.hoverItem === i ? 8 : 5.5,
      fill: kindOf(i.kind)[1],
      className: "DHState-point",
      onmouseenter: () => this.hoverItem = i,
      onmouseleave: () => this.hoverItem = null,
      onclick: () => m.route.set('/atlas/' + i.id)
    }, m("title", null, i.name + (i.what ? ' · ' + i.what : '')))));
  }

  /** Simple vertical bars: [{label, title, value}] */
  bars(rows, color, opts = {}) {
    const max = Math.max(1, ...rows.map(r => r.value || 0));
    return m("div", {
      className: 'DHState-bars' + (opts.compact ? ' is-compact' : '')
    }, rows.map(r => m("div", {
      className: "DHState-barCol",
      title: r.title
    }, m("span", {
      className: "DHState-barNum"
    }, opts.showValues ? r.value : ''), m("span", {
      className: "DHState-bar",
      style: {
        height: Math.max(2, (r.value || 0) / max * 100) + '%',
        background: r.color || color
      }
    }), m("span", {
      className: "DHState-barLabel"
    }, r.label))));
  }

  // ---------------- budget ----------------
  budget(d) {
    const a = d.metrics.state_budget_approved;
    const s = d.metrics.state_budget_actual;
    const years = Array.from(new Set([...(a ? a.history : []), ...(s ? s.history : [])].map(h => h.period))).sort().slice(-6);
    const val = (x, p) => {
      const h = x && x.history.find(r => r.period === p);
      return h ? h.value : null;
    };
    const rows = years.map(p => ({
      period: p,
      approved: val(a, p),
      actual: val(s, p)
    }));
    const max = Math.max(1, ...rows.flatMap(r => [r.approved || 0, r.actual || 0]));
    const last = rows.filter(r => r.approved && r.actual).pop();
    return this.section('budget', 'Budget vs actual spending', 'What the state planned to spend each year and what it actually spent.', m("div", {
      className: "DHPanel"
    }, last ? m("div", {
      className: "DHState-bigRank"
    }, m("strong", null, Math.round(last.actual / last.approved * 100), "%"), m("span", null, "of the ", periodLabel(last.period), " budget was spent (", fmt(last.actual, '₦ bn'), " of ", fmt(last.approved, '₦ bn'), ")")) : null, m("div", {
      className: "DHState-budget"
    }, rows.map(r => m("div", {
      className: "DHState-budgetRow"
    }, m("span", {
      className: "DHState-budgetYear"
    }, periodLabel(r.period)), m("div", {
      className: "DHState-budgetBars"
    }, r.approved !== null ? m("span", {
      className: "is-approved",
      style: {
        width: r.approved / max * 100 + '%'
      },
      title: 'Approved: ' + fmt(r.approved, '₦ bn')
    }) : null, r.actual !== null ? m("span", {
      className: "is-actual",
      style: {
        width: r.actual / max * 100 + '%'
      },
      title: 'Spent: ' + fmt(r.actual, '₦ bn')
    }) : null), m("span", {
      className: "DHState-budgetNums"
    }, r.approved !== null ? fmt(r.approved, '₦ bn') : '—', " / ", r.actual !== null ? fmt(r.actual, '₦ bn') : '—', r.approved && r.actual ? m("strong", null, " ", Math.round(r.actual / r.approved * 100), "%") : null)))), m("p", {
      className: "DHMuted DHState-legendLine"
    }, m("span", {
      className: "DHState-sw is-approved"
    }), " Approved ", m("span", {
      className: "DHState-sw is-actual"
    }), " Actually spent")));
  }

  // ---------------- security ----------------
  security(d) {
    const s = d.security;
    const change = s.prev30 ? pct(s.last30, s.prev30) : null;
    const total = (s.monthly || []).reduce((a, r) => a + r.count, 0);
    return this.section('security', 'Security incidents', 'Crime and security stories about ' + d.state.name + ' reported on Voucha.', m("div", {
      className: "DHPair"
    }, m("div", {
      className: "DHPanel DHState-security"
    }, m("div", {
      className: "DHState-bigRank"
    }, m("strong", null, s.last30), m("span", null, "in the last 30 days"), change !== null ? m("span", {
      className: 'DHChip ' + (change > 0 ? 'is-down' : 'is-up')
    }, change > 0 ? '▲ ' : change < 0 ? '▼ ' : '', Math.abs(change), "% vs the 30 days before (", s.prev30, ")") : s.prev30 === 0 && s.last30 > 0 ? m("span", {
      className: "DHChip is-down"
    }, "none in the 30 days before") : null), s.monthly && s.monthly.length ? this.bars(s.monthly.map(r => ({
      label: monthShort(r.month),
      title: periodLabel(r.month) + ': ' + r.count + ' stories',
      value: r.count
    })), '#b91c1c', {
      showValues: true
    }) : null, m("p", {
      className: "DHMuted DHState-note"
    }, total, " in the last 12 months. Counted from news stories tagged with the state and a crime or security tag, not official crime statistics.")), m("div", {
      className: "DHPanel"
    }, m("h3", null, "Latest incidents"), this.storyList(s.latest, 'No security stories about ' + d.state.name + ' yet.'))));
  }
  storyList(rows, empty) {
    if (!rows || !rows.length) return m("p", {
      className: "DHMuted"
    }, empty);
    return m("ul", {
      className: "DHState-stories"
    }, rows.map(r => m("li", null, link(r.url, r.title), m("span", {
      className: "DHMuted"
    }, dateLabel(r.date), r.replies ? ' · ' + r.replies + (r.replies === 1 ? ' reply' : ' replies') : ''))));
  }

  // ---------------- elections ----------------
  elections(d) {
    const name = d.state.name;
    return this.section('elections', 'Elections', 'The latest election news and polls about ' + name + '.', m("div", {
      className: "DHPair"
    }, m("div", {
      className: "DHPanel"
    }, m("h3", null, "Election news"), this.storyList(d.elections, 'No election stories about ' + name + ' yet.')), m("div", {
      className: "DHPanel DHState-polls"
    }, m("h3", null, "Polls"), (d.polls || []).length ? m("ul", {
      className: "DHState-pollList"
    }, d.polls.map(p => m("li", null, m("span", {
      className: "DHState-pollIcon",
      "aria-hidden": "true"
    }, m("i", {
      className: "fas fa-square-poll-vertical"
    })), m("span", {
      className: "DHState-pollBody"
    }, link(p.url, p.question), m("span", {
      className: "DHMuted"
    }, p.scope === 'state' ? name : 'Politics', ' · ' + p.voters.toLocaleString() + (p.voters === 1 ? ' participant' : ' participants'), p.ended ? ' · Closed' : ' · ' + dateLabel(p.date))), p.ended ? null : link(p.url, 'Vote', 'DHState-pollVote')))) : m("p", {
      className: "DHMuted"
    }, "No polls about ", name, " or politics yet."))));
  }

  // ---------------- power ----------------
  energy(d) {
    const mt = d.metrics;
    const inst = mt.state_power_installed_mw;
    const avail = mt.state_power_available_mw;
    const share = inst && avail && inst.value && avail.period === inst.period ? Math.round(avail.value / inst.value * 100) : null;
    const list = plants(d);
    return this.section('energy', 'Power', 'Grid-connected power plants in ' + d.state.name + ' and how much of their capacity was available.', m("div", null, m("div", {
      className: "DHKpis DHState-tiles"
    }, this.tile(inst, {
      label: 'Installed grid capacity'
    }), this.tile(avail, {
      label: 'Average available capacity',
      note: share !== null ? share + '% of installed capacity was available' : null
    })), list.length ? m("div", {
      className: "DHPanel DHState-plants"
    }, m("h3", null, "Power plants"), m("ul", {
      className: "DHState-list"
    }, list.map(p => m("li", null, m("i", {
      className: "fas fa-bolt DHState-plantIcon",
      "aria-hidden": "true"
    }), m("strong", null, p.name), m("span", {
      className: "DHMuted"
    }, [p.what, p.area].filter(Boolean).join(' · '), p.status && p.status !== 'operational' ? ' · ' + p.status : '')))), m("p", {
      className: "DHMuted DHState-note"
    }, "From the Voucha Atlas. Capacity figures: NERC quarterly reports.")) : null));
  }

  // ---------------- education ----------------
  education(d) {
    const mt = d.metrics;
    const pop = mt.population_2022 ? mt.population_2022.value : null;
    const per = x => x && pop ? x.value / pop * 100000 : null;
    const note = x => per(x) !== null ? Math.round(per(x) * 10) / 10 + ' per 100,000 people' : null;
    return this.section('education', 'Education', 'Schools and higher institutions in the state.', m("div", {
      className: "DHKpis DHState-tiles"
    }, this.tile(mt.state_primary_schools, {
      note: note(mt.state_primary_schools)
    }), this.tile(mt.state_secondary_schools, {
      note: note(mt.state_secondary_schools)
    }), this.tile(mt.state_universities), this.tile(mt.state_polytechnics), this.tile(mt.state_colleges_of_education)));
  }

  // ---------------- neighbours & trade ----------------
  neighbours(d) {
    const t = d.metrics.state_interstate_trade;
    return this.section('neighbours', 'Neighbours & trade', (d.neighbours || []).length ? d.state.name + ' borders ' + d.neighbours.length + (d.neighbours.length === 1 ? ' state.' : ' states.') : null, m("div", {
      className: "DHPair"
    }, (d.neighbours || []).length ? m("div", {
      className: "DHPanel"
    }, m("h3", null, "Neighbouring states"), m("div", {
      className: "DHState-commodities"
    }, d.neighbours.map(n => link('/data-hub/states/' + n.slug, [n.name, m("small", null, " ", n.zone)], 'DHChip DHState-neighbour')))) : null, m("div", {
      className: "DHPanel"
    }, m("h3", null, "Trade with other states"), t ? m("div", {
      className: "DHState-bigRank"
    }, m("strong", null, fmt(t.value, t.unit)), m("span", null, periodLabel(t.period), t.rank ? ' · #' + t.rank + ' of ' + t.of + ' states' : '')) : m("p", {
      className: "DHMuted"
    }, "No trade figures for ", d.state.name, " yet."))));
  }

  // ---------------- investments & big projects ----------------
  money(amount, currency) {
    if (amount === null || amount === undefined) return null;
    const sym = currency === 'USD' ? '$' : '₦';
    return amount >= 1000 ? sym + (Math.round(amount / 10) / 100).toLocaleString('en-US') + 'bn' : sym + (Math.round(amount * 10) / 10).toLocaleString('en-US') + 'm';
  }
  investments(d) {
    const list = d.projects || [];
    const pipe = d.pipeline || [];
    const stage = {
      construction: 'is-building',
      announced: 'is-announced',
      completed: 'is-done',
      stalled: 'is-stalled',
      planned: 'is-announced'
    };
    return this.section('investments', 'Investments & big projects', 'Ongoing investments and major projects in ' + (d.state.isFct ? 'the FCT' : d.state.name) + '.', m("div", null, list.length ? m("div", {
      className: "DHState-projects"
    }, list.map(p => m("div", {
      className: "DHPanel DHState-project"
    }, m("div", {
      className: "DHState-projectHead"
    }, m("span", {
      className: 'DHState-stage ' + (stage[p.status] || '')
    }, p.statusLabel), p.sectorLabel ? m("span", {
      className: "DHMuted"
    }, p.sectorLabel) : null), m("h3", null, p.title), m("p", {
      className: "DHMuted"
    }, [p.backer, this.money(p.amount, p.currency), p.lga ? p.lga + ' LGA' : null, p.expected ? 'due ' + p.expected : null].filter(Boolean).join(' · ')), p.summary ? m("p", {
      className: "DHState-projectText"
    }, p.summary) : null, p.source ? m("a", {
      className: "DHLink",
      href: p.source,
      target: "_blank",
      rel: "noopener nofollow"
    }, "Source ", m("i", {
      className: "fas fa-external-link-alt"
    })) : null))) : m("p", {
      className: "DHMuted"
    }, "No projects recorded for ", d.state.name, " yet."), pipe.length ? m("div", {
      className: "DHPanel DHState-pipeline"
    }, m("h3", null, "On the Voucha Atlas"), m("p", {
      className: "DHMuted"
    }, "Facilities planned or being built in the state."), m("ul", {
      className: "DHState-list"
    }, pipe.map(i => m("li", null, m("span", {
      className: 'DHState-stage ' + (stage[i.status] || '')
    }, i.status === 'construction' ? 'Being built' : 'Planned'), link('/atlas/' + i.id, i.name), m("span", {
      className: "DHMuted"
    }, [i.what, i.online ? 'online ' + i.online.slice(0, 4) : null].filter(Boolean).join(' · ')))))) : null));
  }

  /**
   * Community projects in the state (voucha-community's projects): up to four, with their progress and money raised,
   * and Start a project. Replaces the old "Submit a project" form: investments above stay as Data editors record them.
   */
  communityProjects(d) {
    const name = d.state.isFct ? 'the FCT' : d.state.name;
    // coming soon (voucha-community opens them to members later; admins can already test them)
    if (!app.forum.attribute('vouchaCommunityProjects')) {
      return this.section('community', 'Community projects', 'Coming soon: projects Voucha members run together in ' + name + '.', m("div", {
        className: "DHPanel DHState-soon"
      }, m("p", null, m("strong", null, "Coming soon."), " Members will be able to start projects together in ", name, ", a borehole, a classroom, a road repair, and anyone can follow, vouch for and join them."), m("p", {
        className: "DHMuted"
      }, "For now you can plan your own personal project: a house, land or a business, with ", name, "\u2019s typical prices where we have them."), m("a", {
        className: "Button DHLinkButton",
        href: "/projects/new?type=personal",
        onclick: e => go(e, '/projects/new?type=personal')
      }, m("i", {
        className: "fas fa-plus"
      }), " Start a personal project")));
    }
    if (!this.cp || this.cp.state !== d.state.id) {
      this.cp = {
        state: d.state.id,
        list: null,
        total: 0
      };
      app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/voucha-community/projects?limit=4&state=' + d.state.id,
        errorHandler: () => {}
      }).then(r => {
        this.cp.list = r && r.projects || [];
        this.cp.total = r && r.total || 0;
      }).catch(() => this.cp.list = []).then(() => m.redraw());
    }
    const all = '/projects?state=' + d.state.slug;
    const start = '/projects/new?state=' + d.state.slug;
    const sym = {
      NGN: '₦',
      USD: '$',
      GBP: '£',
      EUR: '€',
      CAD: 'C$'
    };
    const cash = (v, c) => (sym[c] || '₦') + Number(v || 0).toLocaleString('en-US', {
      maximumFractionDigits: 0
    });
    const list = this.cp.list;
    return this.section('community', 'Community projects', 'Projects Voucha members run in ' + name + ': follow them, vouch for them or join in.', m("div", null, list === null ? m(LoadingIndicator, null) : list.length ? m("div", {
      className: "DHState-projects"
    }, list.map(p => m("a", {
      className: "DHPanel DHState-project DHState-cproject",
      href: '/projects/' + p.slug,
      onclick: e => go(e, '/projects/' + p.slug)
    }, m("div", {
      className: "DHState-projectHead"
    }, m("span", {
      className: 'DHState-stage ' + (p.status === 'completed' ? 'is-done' : p.status === 'active' ? 'is-building' : 'is-announced')
    }, p.statusLabel), m("span", {
      className: "DHMuted"
    }, p.kindLabel)), m("h3", null, p.name), m("p", {
      className: "DHMuted"
    }, [p.location, p.lga && p.lga.name].filter(Boolean).join(', ')), p.summary ? m("p", {
      className: "DHState-projectText"
    }, p.summary) : null, m("p", {
      className: "DHMuted"
    }, p.progress, "% done", p.kind !== 'monitoring' && p.funding && p.funding.required > 0 ? ' · ' + cash(p.funding.received, p.currency) + ' of ' + cash(p.funding.required, p.currency) + ' raised' : '', " \xB7 ", p.followerCount, " following")))) : m("p", {
      className: "DHMuted"
    }, "No community projects in ", name, " yet."), m("a", {
      className: "Button DHLinkButton",
      href: start,
      onclick: e => go(e, start)
    }, m("i", {
      className: "fas fa-plus"
    }), " Start a project in ", name)), this.cp.total > (list || []).length ? m("a", {
      className: "DHLink",
      href: all,
      onclick: e => go(e, all)
    }, "All ", this.cp.total, " \u203A") : null);
  }

  /** Businesses members have listed in the state (the business directory) */
  businesses(d) {
    const name = d.state.isFct ? 'the FCT' : d.state.name;
    const dir = '/businesses?country=NG&state=' + d.state.id;
    return this.section('businesses', 'Businesses', 'Businesses Voucha members have listed in ' + name + ', by industry.', businessPanel({
      country: 'NG',
      state: d.state.id
    }, name, 'country=NG&state=' + d.state.id), m("a", {
      className: "DHLink",
      href: dir,
      onclick: e => go(e, dir)
    }, "Directory \u203A"));
  }
}